/**
 * Team teaser - Figma `6377:86971`, on the home page `3883:70468`.
 *
 * A large heading on the left, a collage of photographs on the right, and a row of pill
 * links underneath.
 *
 * **The collage is a fixed four-slot layout, and it has to be.** The photographs are
 * placed by eye in the file at four different sizes with four different offsets
 * (`3883:70473`, 405x535); no rule about aspect ratios reproduces it, and an earlier
 * attempt to approximate it with a two-column grid and a top margin was visibly not the
 * design. So the four slots are measured off the file and expressed as percentages of a
 * 405x535 box, which keeps the arrangement while letting it scale:
 *
 * | Slot | left | top | width | height |
 * |---|---|---|---|---|
 * | 1 | 0% | 12.7% | 40.0% | 31.8% |
 * | 2 | 42.0% | 0% | 58.0% | 43.7% |
 * | 3 | 0% | 45.6% | 25.7% | 35.7% |
 * | 4 | 27.9% | 45.6% | 72.1% | 54.4% |
 *
 * Fewer than four photographs fill the slots in order and leave the rest empty, which is
 * how the design would look with a gap rather than a rearrangement. More than four are
 * ignored: there are four slots.
 *
 * Below `lg` the collage becomes a plain two-column grid of squares - the offsets are
 * a desktop composition, and at 320px wide they would be four slivers.
 *
 * **The section gained `links`.** The design ends with four links; the model had one
 * `cta`. `cta` is kept for the sections already using it, and renders after the pills
 * when both are filled in.
 */
import { CtaLink, Eyebrow, LinkPills, SectionShell, withHighlight } from '../primitives';
import type { StrapiMedia, TeamTeaserSection } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';

/** Measured off `3883:70473`. Percentages of the 405x535 box. */
const SLOTS = [
  { left: '0%', top: '12.71%', width: '40.00%', height: '31.78%' },
  { left: '41.98%', top: '0%', width: '58.02%', height: '43.74%' },
  { left: '0%', top: '45.61%', width: '25.68%', height: '35.70%' },
  { left: '27.90%', top: '45.61%', width: '72.10%', height: '54.39%' }
];

function Collage({ images }: { images: StrapiMedia[] }) {
  return (
    <>
      {/* Desktop: the design's arrangement. */}
      <div className="relative hidden aspect-[405/535] w-full lg:block">
        {images.slice(0, SLOTS.length).map((image, i) => (
          <figure key={i} className="absolute m-0 overflow-hidden rounded-s bg-surface-sunken" style={SLOTS[i]}>
            <Image
              media={image}
              alt={image.alternativeText ?? ''}
              sizes={SIZES.third}
              className="h-full w-full object-cover"
            />
          </figure>
        ))}
      </div>

      {/* Narrow: a plain grid, because the offsets are a desktop composition. */}
      <div className="grid grid-cols-2 gap-3 lg:hidden">
        {images.map((image, i) => (
          <figure key={i} className="m-0 aspect-square overflow-hidden rounded-s bg-surface-sunken">
            <Image
              media={image}
              alt={image.alternativeText ?? ''}
              sizes={SIZES.half}
              className="h-full w-full object-cover"
            />
          </figure>
        ))}
      </div>
    </>
  );
}

export default function TeamTeaser({ data }: { data: TeamTeaserSection }) {
  const images = data.images ?? [];

  return (
    <SectionShell settings={data.settings} name="team-teaser">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}

      {/* 900px of heading against 405px of collage, on a 1360px grid. */}
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_405px] lg:gap-14">
        <div className="flex flex-col gap-6">
          <h2 className="text-h3 text-ink lg:text-h2">{withHighlight(data.heading)}</h2>
          {data.body ? <p className="whitespace-pre-line text-body text-ink-muted">{data.body}</p> : null}
        </div>

        {images.length ? <Collage images={images} /> : null}
      </div>

      <div className="mt-6 flex flex-col gap-6">
        <LinkPills items={data.links} />
        {data.cta ? <CtaLink value={data.cta} /> : null}
      </div>
    </SectionShell>
  );
}
